import { FaFacebook, FaInstagram, FaYoutube, FaTwitter } from "react-icons/fa";
import { useState, useRef } from "react";
import { API_ENDPOINTS } from "@/constants/constants";
import ReCAPTCHA from "react-google-recaptcha";

export default function Footer() {
  // Add state for form inputs
  const [formData, setFormData] = useState({
    email: "",
    tel: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaValue) {
      alert("Veuillez vérifier que vous n'êtes pas un robot");
      return;
    }

    // Validate form data
    if (!formData.email || !formData.message) {
      setSubmitStatus({
        success: false,
        message: "Veuillez remplir tous les champs obligatoires.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      const response = await fetch(API_ENDPOINTS.CONTACT.SEND_MESSAGE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken: captchaValue,
        }),
      });

      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: "Votre message a été envoyé avec succès!",
        });
        // Reset form after successful submission
        setFormData({ email: "", tel: "", message: "" });
        recaptchaRef.current?.reset();
        setCaptchaValue(null);
      } else {
        setSubmitStatus({
          success: false,
          message: "Une erreur s'est produite. Veuillez réessayer plus tard.",
        });
      }
    } catch {
      setSubmitStatus({
        success: false,
        message:
          "Une erreur de connexion s'est produite. Veuillez vérifier votre connexion internet.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#41a873] text-white py-8 mobile:py-4 tablet:py-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start justify-around px-6 gap-8 mobile:gap-4 tablet:gap-6">
        {/* Bloc gauche : Événements & Réseaux Sociaux */}
        <div className="w-full md:w-auto">
          <div className="flex flex-col sm:flex-row gap-1 pt-20 mobile:flex-col mobile:items-stretch mobile:pt-5 tablet:flex-col tablet:items-stretch tablet:pt-5">
            <div className="bg-[#0b9d49] text-white p-6 flex flex-1 items-center justify-center mobile:mb-2 tablet:mb-2">
              <p className="text-center font-bold uppercase mobile:text-sm tablet:text-base">
                Participer à nos événements
              </p>
            </div>
            <div className="grid grid-cols-2 h-full gap-1 mobile:grid-cols-4 tablet:grid-cols-4">
              <a
                href="#"
                className="bg-[#3b5998] flex justify-center items-center px-12 py-6 mobile:px-4 mobile:py-4 tablet:px-6 tablet:py-5"
              >
                <FaFacebook size={24} color="white" />
              </a>
              <a
                href="#"
                className="bg-[#8a3ab9] flex justify-center items-center px-12 py-6 mobile:px-4 mobile:py-4 tablet:px-6 tablet:py-5"
              >
                <FaInstagram size={24} color="white" />
              </a>
              <a
                href="#"
                className="bg-[#FF0000] flex justify-center items-center px-12 py-6 mobile:px-4 mobile:py-4 tablet:px-6 tablet:py-5"
              >
                <FaYoutube size={24} color="white" />
              </a>
              <a
                href="#"
                className="bg-black flex justify-center items-center px-12 py-6 mobile:px-4 mobile:py-4 tablet:px-6 tablet:py-5"
              >
                <FaTwitter size={24} color="white" />
              </a>
            </div>
          </div>

          {/* Liens en bas */}
          <div className="text-center mt-6 text-sm mobile:flex mobile:flex-col mobile:items-center tablet:flex tablet:flex-col tablet:items-center">
            <div className="mobile:space-y-2 tablet:space-y-2">
              <a href="#" className="mx-2 mobile:block tablet:block">
                À propos
              </a>
              <a href="#" className="mx-2 mobile:block tablet:block">
                Conditions d'utilisation
              </a>
              <a href="#" className="mx-2 mobile:block tablet:block">
                Confidentialité
              </a>
            </div>
          </div>
        </div>

        {/* Bloc droit : Formulaire de contact */}
        <div className="bg-white text-gray-800 p-6 rounded-lg shadow-lg w-full md:w-1/3 mobile:mt-6 mobile:w-full mobile:p-4 tablet:mt-6 tablet:w-full tablet:p-5">
          <h3 className="text-green-700 font-bold text-lg mb-4 mobile:text-base tablet:text-lg">
            NOUS CONTACTER
          </h3>
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@gmail.com"
              className="w-full p-2 mb-1 border rounded mobile:text-sm tablet:text-base"
            />
            <input
              type="tel"
              name="tel"
              value={formData.tel}
              onChange={handleChange}
              placeholder="+2250700000000"
              className="w-full p-2 mb-1 border rounded mobile:text-sm tablet:text-base"
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Entrez votre message"
              className="w-full p-2 mb-2 border rounded mobile:text-sm tablet:text-base"
            ></textarea>

            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_RECAPTCHA_SITEKEY}
                onChange={handleCaptchaChange}
                className="transform scale-90 sm:scale-100"
              />
            </div>

            {submitStatus.message && (
              <div
                className={`text-sm mb-2 ${
                  submitStatus.success ? "text-green-600" : "text-red-500"
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={!captchaValue || isSubmitting}
              className="bg-green-600 text-white w-full py-2 rounded hover:bg-green-800 mobile:text-sm tablet:text-base disabled:bg-gray-400"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer"}
            </button>
          </form>
        </div>
      </div>
      {/* Copyright */}
      <p className="text-center text-xs mt-4 mobile:text-xs tablet:text-sm">
        © Copyright 2025 | Mentions légales | Ministère de la Fonction Publique
        et de la Modernisation de l'Administration
      </p>
    </footer>
  );
}
